import { BLISS_CUSTOM_FUNNEL_URL, BLISS_BASIC_PUBLIC_ORIGIN } from './blissBasicHost'
import { BLISS_DESIGN_HANDOFF_SOURCE, type BlissRoomPlanAttribution } from './blissRoomPlan'

export const BLISS_DESIGN_PUBLIC_ORIGIN = 'https://design.blissbunkbeds.com'

export function isBlissDesignHost(hostname?: string | null): boolean {
  const h = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase()
  return h === 'design.blissbunkbeds.com' || h.startsWith('design.')
}

/** Landing / marketing CTA — always open step 1, not a resumed mid-flow plan. */
export function blissDesignStudioFreshEntryUrl(search = '', hostname?: string | null): string {
  const params = new URLSearchParams(search.replace(/^\?/, ''))
  params.delete('plan_id')
  params.set('new', '1')
  return blissDesignStudioEntryUrl(params.toString(), hostname)
}

export type BlissDesignStudioFunnelRedirectOptions = {
  search?: string
  planId?: string | null
  blissQuoteId?: string | null
  hostname?: string | null
}

/** After the design contact gate — open Bunkroom Design Studio (resume plan when plan_id is present). */
export function blissDesignStudioFunnelRedirectUrl(
  options: BlissDesignStudioFunnelRedirectOptions = {},
): string {
  const params = new URLSearchParams((options.search ?? '').replace(/^\?/, ''))
  params.delete('funnel_step')
  params.delete('funnel_tier')
  params.delete('funnel_media_token')
  params.delete('funnel_scroll')
  params.delete('design_canceled')
  params.delete('offer')
  params.delete('ref')
  params.delete('quote')
  params.delete('quoteId')
  params.delete('quote_id')
  params.delete('source')

  const planId = options.planId?.trim() || params.get('plan_id')?.trim()
  const blissQuoteId = options.blissQuoteId?.trim()
  if (blissQuoteId) params.set('bliss_quote_id', blissQuoteId)

  if (planId) {
    params.set('plan_id', planId)
    params.delete('new')
    return blissDesignStudioEntryUrl(params.toString(), options.hostname)
  }

  return blissDesignStudioFreshEntryUrl(params.toString(), options.hostname)
}

/** Entry URL for Bunkroom Design Studio (design subdomain; `/design` on local dev). */
export function blissDesignStudioEntryUrl(search = '', hostname?: string | null): string {
  const h = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase()
  const query = search.replace(/^\?/, '')
  if (h === 'localhost' || h.endsWith('.local')) {
    return query ? `/design?${query}` : '/design'
  }
  const url = new URL('/', BLISS_DESIGN_PUBLIC_ORIGIN)
  if (query) url.search = query
  return url.href.replace(/\/\?/, '?').replace(/\/$/, '')
}

export function blissBasicPackageOfferingsUrl(planId: string): string {
  const q = new URLSearchParams()
  q.set('plan_id', planId)
  q.set('source', BLISS_DESIGN_HANDOFF_SOURCE)
  return `${BLISS_BASIC_PUBLIC_ORIGIN}/options/packages?${q.toString()}`
}

/** After the room plan — Basic packages in the shared proposal and checkout funnel. */
export function blissBasicHandoffUrl(planId: string): string {
  const q = new URLSearchParams()
  q.set('plan_id', planId)
  q.set('source', BLISS_DESIGN_HANDOFF_SOURCE)
  q.set('product_line', 'basic')
  return `${BLISS_CUSTOM_FUNNEL_URL}/?${q.toString()}`
}

/** After paint/finish is chosen — return to Bunkroom Design Studio (no access/timeline wizard). */
export function blissBasicFinishHandoffUrl(search = '', hostname?: string | null): string {
  const params = new URLSearchParams(search.replace(/^\?/, ''))
  const planId = params.get('plan_id')?.trim()
  if (planId) {
    return blissDesignStudioEntryUrl(params.toString(), hostname)
  }
  return blissDesignStudioFreshEntryUrl(params.toString(), hostname)
}

export function blissCustomHandoffUrl(planId: string): string {
  const q = new URLSearchParams()
  q.set('plan_id', planId)
  q.set('source', BLISS_DESIGN_HANDOFF_SOURCE)
  q.set('product_line', 'custom')
  return `${BLISS_CUSTOM_FUNNEL_URL}/?${q.toString()}`
}

/** Read UTM params from the current URL into attribution shape. */
export function readAttributionFromLocation(search: string): BlissRoomPlanAttribution {
  const params = new URLSearchParams(search)
  const pick = (key: string) => params.get(key)?.trim() || undefined
  return {
    source: 'design.blissbunkbeds.com',
    utmSource: pick('utm_source'),
    utmMedium: pick('utm_medium'),
    utmCampaign: pick('utm_campaign'),
    utmContent: pick('utm_content'),
    utmTerm: pick('utm_term'),
  }
}
